import { getAccessToken } from '../utils/storage';
import { authFetch, getApiUrl } from '../utils/api';
import { friendlyApiError } from '../utils/apiErrors';

export interface VacancyReportColumn {
  key: string;
  label: string;
  numeric?: boolean;
  hidden?: boolean;
}

export interface VacancyChartPoint {
  label: string;
  value: number;
}

export interface VacancyReportCharts {
  open_by_store: VacancyChartPoint[];
  open_by_province: VacancyChartPoint[];
  ageing_by_store: VacancyChartPoint[];
  ageing_by_province: VacancyChartPoint[];
}

export interface VacancyReportResponse {
  key: string;
  title: string;
  rows: Record<string, unknown>[];
  columns: VacancyReportColumn[];
  summary?: {
    row_count?: number;
    open_count?: number;
    average_ageing_days?: number;
    ageing_scope?: string;
    numeric_totals?: Record<string, number>;
  };
  charts?: VacancyReportCharts;
}

const makeHeaders = (): Record<string, string> => {
  const token = getAccessToken();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
  };

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

export const fetchVacancyReport = async (
  reportKey: string,
  limit = 5000,
  ageingScope: 'open' | 'all' = 'open',
): Promise<VacancyReportResponse> => {
  const params = new URLSearchParams({ limit: String(limit), ageing_scope: ageingScope });

  const response = await authFetch(getApiUrl(`/v1/vacancy-report/${reportKey}?${params.toString()}`), {
    method: 'GET',
    headers: makeHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Vacancy Report'));
  }

  const data: VacancyReportResponse = await response.json();
  return {
    ...data,
    rows: Array.isArray(data.rows) ? data.rows : [],
    columns: Array.isArray(data.columns) ? data.columns : [],
  };
};

export type VacancyReportSyncStatus = 'running' | 'succeeded' | 'failed' | 'partial';

export interface VacancyReportSyncRun {
  run_id: number;
  status: VacancyReportSyncStatus;
  triggered_by?: string;
  started_at?: string | null;
  completed_at?: string | null;
  excel_row_count?: number | null;
  sage_row_count?: number | null;
  vacancies_opened?: number;
  vacancies_closed?: number;
  overstaffing_opened?: number;
  overstaffing_closed?: number;
  data_quality_alert_count?: number;
  error_message?: string | null;
}

// Kicks off a reconcile and returns immediately with the new run's id (HTTP
// 202). The sync runs in the background on the server; poll
// fetchVacancyReportSyncStatus(run_id) until status is no longer 'running'.
export const triggerVacancyReportSync = async (): Promise<VacancyReportSyncRun> => {
  const response = await authFetch(getApiUrl('/v1/vacancy-report/sync'), {
    method: 'POST',
    headers: makeHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Vacancy Report'));
  }

  return response.json();
};

export const fetchVacancyReportSyncStatus = async (runId: number): Promise<VacancyReportSyncRun> => {
  const params = new URLSearchParams({ run_id: String(runId) });
  const response = await authFetch(getApiUrl(`/v1/vacancy-report/sync/status?${params.toString()}`), {
    method: 'GET',
    headers: makeHeaders(),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(friendlyApiError(response.status, errorData.detail, 'The Vacancy Report'));
  }

  return response.json();
};
